import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  Observable,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { Injectable } from '@angular/core';
import { AuthService } from '../../app/modules/auth/services/auth.service';
import { Router } from '@angular/router';
import { SETTINGS } from '../configs/common.settings';
import { getItem } from '../utils/safe-storage';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  constructor(public authService: AuthService, private router: Router) { }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (this.authService.getToken() && !request.headers.has('Authorization')) {
      request = this.addToken(request, this.authService.getToken());
    } else {
      request = this.addGeneralHeaders(request);
    }

    return next.handle(request).pipe(
      catchError(error => {
        const url = request.url || '';
        const isAuthCredentialCall =
          url.includes('/user/login') ||
          url.includes('/user/refresh-token') ||
          url.includes('/user/request-reset-password') ||
          url.includes('/user/reset-password') ||
          url.includes('/user/validate-reset-password');
        // Shield uses 403 for both JWT expiry (code 1003) and permission denial (1004).
        // Only refresh on real JWT expiry — otherwise permission 403s loop forever and
        // hang spinners (e.g. pipeline monthly report).
        const errCode = (error as HttpErrorResponse)?.error?.code;
        const isJwtExpired =
          error instanceof HttpErrorResponse &&
          error.status === 403 &&
          (errCode === 1003 ||
            String((error as HttpErrorResponse)?.error?.message || '')
              .toLowerCase()
              .includes('jwt expired'));
        if (
          isJwtExpired &&
          this.authService.isLoggedIn() &&
          !isAuthCredentialCall
        ) {
          return this.handleAccessTokenExpiredError(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private activeCompanyHeaders(): Record<string, string> {
    const companyId = getItem(SETTINGS.COMPANY);
    return companyId ? { 'X-Active-Company': companyId } : {};
  }

  /* Headers with Token */
  private addToken(request: HttpRequest<any>, token: any): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        // 'Content-Type': 'multipart/form-data',
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...this.activeCompanyHeaders(),
      },
    });
  }

  /* Headers without Token */
  private addGeneralHeaders(request: HttpRequest<any>): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        // 'Content-Type': 'application/json; charset=utf-8',
        Accept: 'application/json',
        ...this.activeCompanyHeaders(),
      },
    });
  }

  /* Re-send the previous request after refreshing the access token */
  private handleAccessTokenExpiredError(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((token: any) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(token.access_token);
          return next.handle(this.addToken(request, token.access_token));
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(jwt => {
          return next.handle(this.addToken(request, jwt));
        })
      );
    }
  }
}
